import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { unifiedLogin, unifiedPhoneLogin, unifiedPhoneMpinLogin, requestOTP, resetMpinWithOtp, getTenantByCode } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getFirstAvailableRoute } from '../utils/routeUtils';

interface LoginConfig {
  formDisplaySide: 'left' | 'right';
  screenBackground?: string;
  pageLogo?: string;
  formLogo?: string;
  ownerImage?: string;
  ownerName?: string;
  ownerDesignation?: string;
  title?: string;
  subtitle?: string;
}

interface CustomLoginProps {
  tenantCode: string;
  loginConfig: LoginConfig;
}

const CustomLogin: React.FC<CustomLoginProps> = ({ tenantCode, loginConfig }) => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [phoneAuthMode, setPhoneAuthMode] = useState<'otp' | 'mpin' | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mPin, setMPin] = useState('');
  const [showMPin, setShowMPin] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    phone: '',
    password: '',
    mPin: '',
  });
  const [forgotMpinMode, setForgotMpinMode] = useState<'idle' | 'confirm' | 'otpAndNew'>('idle');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewMpin, setForgotNewMpin] = useState('');
  const [forgotConfirmMpin, setForgotConfirmMpin] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [viewport, setViewport] = useState({ width: 1280, height: 720 });
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isSystemAdmin, isTenantAdmin, hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Check screen size and track viewport for scaling
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth || 1280;
      const height = window.innerHeight || 720;
      setViewport({ width, height });
      setIsMobile(width < 1024); // Desktop breakpoint at 1024px
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Prefill from navigation state (e.g. after reset MPIN / change password)
  useEffect(() => {
    const state = (location.state || {}) as { phone?: string; email?: string; loginMethod?: 'email' | 'phone' };
    if (state.phone) {
      setPhone(state.phone);
    }
    if (state.email) {
      setEmail(state.email);
    }
    if (state.loginMethod === 'phone') {
      setLoginMethod('phone');
      setPhoneAuthMode('mpin');
    }
    if (state.loginMethod === 'email') {
      setLoginMethod('email');
    }
  }, [location.state]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      if (isSystemAdmin) {
        navigate('/dashboard/tenants', { replace: true });
      } else if (isTenantAdmin) {
        navigate('/dashboard', { replace: true });
      } else {
        const firstRoute = getFirstAvailableRoute(hasPermission, false, false);
        navigate(firstRoute, { replace: true });
      }
    }
  }, [isAuthenticated, isSystemAdmin, isTenantAdmin, hasPermission, navigate]);

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return 'Phone is required';
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  const validateMPin = (v: string): string => {
    if (!v || !v.trim()) return 'MPIN is required';
    if (!/^[0-9]{4,6}$/.test(v.trim())) return 'MPIN must be 4 to 6 digits (numbers only)';
    return '';
  };

  const handleRequestOTP = async () => {
    setError('');
    setSuccess('');
    
    // Validate tenantCode
    if (!tenantCode) {
      setError('Tenant code is required');
      return;
    }
    
    const phoneError = validatePhone(phone);
    if (phoneError) {
      setValidationErrors({ ...validationErrors, phone: phoneError });
      setError(phoneError);
      return;
    }

    try {
      setPhoneAuthMode('otp');
      setLoading(true);
      await requestOTP(phone, tenantCode);
      setOtpSent(true);
      setSuccess('OTP sent successfully to your phone');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tenant ID on mount
  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const tenantResponse = await getTenantByCode(tenantCode);
        const tenant = tenantResponse.data.data;
        if (tenant && tenant._id) {
          setTenantId(tenant._id);
        }
      } catch (err: any) {
        console.error('Failed to fetch tenant ID:', err);
      }
    };
    fetchTenantId();
  }, [tenantCode]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!tenantId) {
      setError('Tenant information not available. Please refresh the page.');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (loginMethod === 'email') {
        const emailError = validateEmail(email);
        const passwordError = validatePassword(password);
        if (emailError || passwordError) {
          setValidationErrors({ ...validationErrors, email: emailError, password: passwordError });
          setError(emailError || passwordError);
          setLoading(false);
          return;
        }
        response = await unifiedLogin(email, password, tenantId!);
      } else if (loginMethod === 'phone') {
        const phoneError = validatePhone(phone);
        if (phoneError) {
          setValidationErrors({ ...validationErrors, phone: phoneError });
          setError(phoneError);
          setLoading(false);
          return;
        }
        if (phoneAuthMode === 'otp') {
          if (!otpSent) {
            setError('Please request OTP first');
            setLoading(false);
            return;
          }
          response = await unifiedPhoneLogin(phone, otp, tenantId!);
        } else if (phoneAuthMode === 'mpin') {
          const mPinError = validateMPin(mPin);
          if (mPinError) {
            setValidationErrors({ ...validationErrors, mPin: mPinError });
            setError(mPinError);
            setLoading(false);
            return;
          }
          response = await unifiedPhoneMpinLogin(phone, mPin, tenantId!);
        } else {
          setError('Choose Request OTP or MPIN');
          setLoading(false);
          return;
        }
      }

      if (!response) return;
      const { token, user } = response!.data.data;
      login(token, user);

      await new Promise(resolve => setTimeout(resolve, 200));

      const isSysAdmin = user.isSystem || false;
      const isTenAdmin =
        user.type === 'tenant_level_admin' ||
        user.type === 'tenant_admin' ||
        (user as any).roles?.includes('tenantAdmin') ||
        user.role === 'tenantAdmin' ||
        false;
      
      if (isSysAdmin) {
        navigate('/dashboard/tenants', { replace: true });
      } else if (isTenAdmin) {
        navigate('/dashboard', { replace: true });
      } else {
        const firstRoute = getFirstAvailableRoute(hasPermission, false, false);
        navigate(firstRoute, { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Login failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isFormLeft = loginConfig.formDisplaySide === 'left';
  const desktopScale = !isMobile
    ? Math.min(viewport.width / 1440, viewport.height / 900, 1)
    : 1;

  return (
    <div style={{
      height: '100vh',
      minHeight: '100vh',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Image */}
      {loginConfig.screenBackground && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${loginConfig.screenBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(2px)',
          zIndex: 0
        }} />
      )}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isDark 
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.85) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(240, 240, 240, 0.85) 100%)',
        zIndex: 1
      }} />

      {/* Content Container */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 2,
          padding: isMobile ? '16px' : '0',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            width: isMobile ? '100%' : 'min(1200px, 100vw)',
            maxHeight: '100vh',
            transform: !isMobile ? `scale(${desktopScale})` : undefined,
            transformOrigin: 'center center',
          }}
        >
        {/* Form Side */}
        <div style={{
          width: isMobile ? '100%' : '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '20px' : '40px',
          order: isFormLeft ? 1 : 2
        }}>
          <div style={{
            width: '100%',
            maxWidth: '450px',
            background: 'white',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Form Logo */}
            {loginConfig.formLogo && (
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <img 
                  src={loginConfig.formLogo} 
                  alt="Logo" 
                  style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
                />
              </div>
            )}

            {error && (
              <div style={{
                background: '#fee',
                border: '2px solid #e74c3c',
                color: '#c33',
                padding: '14px 18px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '15px',
                fontWeight: '500'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                background: '#e8f5e9',
                border: '1px solid #4caf50',
                color: '#2e7d32',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '15px'
              }}>
                {success}
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('email');
                    setError('');
                    setSuccess('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: loginMethod === 'email' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                    color: loginMethod === 'email' ? 'white' : '#666',
                    border: loginMethod === 'email' ? 'none' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  📧 Email
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod('phone');
                    setError('');
                    setSuccess('');
                    setPhoneAuthMode('mpin');
                    setOtpSent(false);
                    setOtp('');
                    setMPin('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: loginMethod === 'phone' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                    color: loginMethod === 'phone' ? 'white' : '#666',
                    border: loginMethod === 'phone' ? 'none' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  📱 Phone
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {loginMethod === 'email' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600',
                      color: '#333',
                      fontSize: '15px'
                    }}>
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setValidationErrors({ ...validationErrors, email: '' });
                      }}
                      placeholder="Enter your email"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '16px',
                        border: validationErrors.email ? '2px solid #e74c3c' : '2px solid #e0e0e0',
                        borderRadius: '8px',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s'
                      }}
                      required
                    />
                    {validationErrors.email && (
                      <small style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.email}
                      </small>
                    )}
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600',
                      color: '#333',
                      fontSize: '15px'
                    }}>
                      Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setValidationErrors({ ...validationErrors, password: '' });
                        }}
                        placeholder="Enter your password"
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          paddingRight: '50px',
                          fontSize: '16px',
                          border: validationErrors.password ? '2px solid #e74c3c' : '2px solid #e0e0e0',
                          borderRadius: '8px',
                          boxSizing: 'border-box'
                        }}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: '#666'
                        }}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {validationErrors.password && (
                      <small style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.password}
                      </small>
                    )}
                  </div>
                  <div style={{ marginTop: '-8px', marginBottom: '20px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/change-password', {
                          state: {
                            email,
                            tenantCode,
                          },
                        });
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        color: '#667eea',
                        fontSize: '14px',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Change Password?
                    </button>
                  </div>
                </>
              )}

              {loginMethod === 'phone' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={phone}
                      onChange={(e) => {
                        const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setPhone(numeric);
                        setValidationErrors({ ...validationErrors, phone: '' });
                      }}
                      placeholder="Enter your phone number"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        fontSize: '16px',
                        border: validationErrors.phone ? '2px solid #e74c3c' : '2px solid #e0e0e0',
                        borderRadius: '8px',
                        boxSizing: 'border-box'
                      }}
                      maxLength={10}
                      required
                    />
                    {validationErrors.phone && (
                      <small style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', display: 'block' }}>{validationErrors.phone}</small>
                    )}
                  </div>
                  {phoneAuthMode === 'otp' && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '15px' }}>OTP</label>
                      <input
                        type="text"
                        id="otp"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter OTP"
                        maxLength={6}
                        required
                        style={{
                          width: '100%',
                          padding: '14px 16px',
                          fontSize: '16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                  {phoneAuthMode === 'mpin' && (
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '15px' }}>MPIN</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showMPin ? 'text' : 'password'}
                          id="mpin"
                          inputMode="numeric"
                          autoComplete="off"
                          value={mPin}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setMPin(digits);
                            setValidationErrors({ ...validationErrors, mPin: '' });
                          }}
                          placeholder="4–6 digit MPIN"
                          required
                          maxLength={6}
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            paddingRight: '50px',
                            fontSize: '16px',
                            border: validationErrors.mPin ? '2px solid #e74c3c' : '2px solid #e0e0e0',
                            borderRadius: '8px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowMPin(!showMPin)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: '#666'
                          }}
                        >
                          {showMPin ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                      {validationErrors.mPin && (
                        <small style={{ color: '#e74c3c', fontSize: '13px', marginTop: '4px', display: 'block' }}>{validationErrors.mPin}</small>
                      )}
                    </div>
                  )}
                  {phoneAuthMode === 'mpin' && (
                    <div style={{ marginTop: '-8px', marginBottom: '20px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          navigate('/reset-mpin', {
                            state: {
                              phone,
                              tenantCode,
                            },
                          });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          color: '#667eea',
                          fontSize: '14px',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        Forgot MPIN?
                      </button>
                    </div>
                  )}

                  {forgotMpinMode !== 'idle' && (
                    <div
                      style={{
                        marginBottom: '24px',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', color: '#0f172a' }}>
                        Reset MPIN
                      </div>
                      {forgotMpinMode === 'confirm' && (
                        <>
                          <p style={{ fontSize: '13px', marginBottom: '10px', color: '#475569' }}>
                            Confirm your phone number and tenant code to receive an OTP.
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                            <input
                              type="tel"
                              value={phone}
                              onChange={(e) => {
                                const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setPhone(numeric);
                              }}
                              placeholder="Phone number"
                              maxLength={10}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                              }}
                            />
                            <input
                              type="text"
                              value={tenantCode}
                              onChange={() => {}}
                              readOnly
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '14px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: '#e2e8f0',
                                textTransform: 'uppercase',
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                setError('');
                                setSuccess('');
                                const phoneError = validatePhone(phone);
                                if (phoneError) {
                                  setValidationErrors({ ...validationErrors, phone: phoneError });
                                  setError(phoneError);
                                  return;
                                }
                                try {
                                  setLoading(true);
                                  await requestOTP(phone, tenantCode);
                                  setSuccess('OTP sent successfully to your phone');
                                  setForgotMpinMode('otpAndNew');
                                } catch (err: any) {
                                  const errorMessage = err.response?.data?.message || 'Failed to send OTP';
                                  setError(errorMessage);
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading || !phone}
                              style={{
                                flex: 1,
                                padding: '10px 12px',
                                fontSize: '14px',
                                fontWeight: 600,
                                background: (loading || !phone) ? '#cbd5e1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: (loading || !phone) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {loading ? 'Sending...' : 'Send OTP'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setForgotMpinMode('idle');
                                setForgotOtp('');
                                setForgotNewMpin('');
                                setForgotConfirmMpin('');
                              }}
                              style={{
                                padding: '10px 12px',
                                fontSize: '14px',
                                background: 'transparent',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                color: '#64748b',
                                cursor: 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                      {forgotMpinMode === 'otpAndNew' && (
                        <>
                          <p style={{ fontSize: '13px', marginBottom: '10px', color: '#475569' }}>
                            Enter the OTP sent to your phone and choose a new MPIN.
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                            <input
                              type="text"
                              value={forgotOtp}
                              onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="6-digit OTP"
                              maxLength={6}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                letterSpacing: '4px',
                                textAlign: 'center',
                              }}
                            />
                            <input
                              type="password"
                              value={forgotNewMpin}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setForgotNewMpin(digits);
                              }}
                              placeholder="New 4–6 digit MPIN"
                              maxLength={6}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                              }}
                            />
                            <input
                              type="password"
                              value={forgotConfirmMpin}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setForgotConfirmMpin(digits);
                              }}
                              placeholder="Confirm new MPIN"
                              maxLength={6}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                setError('');
                                setSuccess('');
                                if (!forgotOtp || forgotOtp.length !== 6) {
                                  setError('Please enter a valid 6-digit OTP');
                                  return;
                                }
                                if (!forgotNewMpin || forgotNewMpin.length < 4) {
                                  setError('New MPIN must be 4 to 6 digits');
                                  return;
                                }
                                if (forgotNewMpin !== forgotConfirmMpin) {
                                  setError('New MPIN and confirm MPIN do not match');
                                  return;
                                }
                                try {
                                  setLoading(true);
                                  await resetMpinWithOtp(phone, tenantCode, forgotOtp, forgotNewMpin);
                                  setSuccess('MPIN updated successfully. Please login with your new MPIN.');
                                  setForgotMpinMode('idle');
                                  setForgotOtp('');
                                  setForgotNewMpin('');
                                  setForgotConfirmMpin('');
                                  setPhoneAuthMode('mpin');
                                } catch (err: any) {
                                  const errorMessage = err.response?.data?.message || 'Failed to reset MPIN';
                                  setError(errorMessage);
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={loading}
                              style={{
                                flex: 1,
                                padding: '10px 12px',
                                fontSize: '14px',
                                fontWeight: 600,
                                background: loading ? '#cbd5e1' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {loading ? 'Saving...' : 'Save New MPIN'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setForgotMpinMode('confirm');
                                setForgotOtp('');
                                setForgotNewMpin('');
                                setForgotConfirmMpin('');
                              }}
                              style={{
                                padding: '10px 12px',
                                fontSize: '14px',
                                background: 'transparent',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                color: '#64748b',
                                cursor: 'pointer',
                              }}
                            >
                              Back
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '16px',
                    fontSize: '18px',
                    fontWeight: '700',
                    background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.2s'
                  }}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Content Side - Hidden on mobile */}
        {!isMobile && (
          <div style={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            color: isDark ? 'white' : '#1a1a1a',
            order: isFormLeft ? 2 : 1
          }}>
          {/* Page Logo */}
          {loginConfig.pageLogo && (
            <div style={{ marginBottom: '40px', textAlign: 'center' }}>
              <img 
                src={loginConfig.pageLogo} 
                alt="Logo" 
                style={{ maxWidth: '300px', maxHeight: '120px', objectFit: 'contain' }}
              />
            </div>
          )}

          {/* Title */}
          {loginConfig.title && (
            <h1 style={{
              fontSize: '48px',
              fontWeight: '700',
              marginBottom: '16px',
              textAlign: 'center',
              lineHeight: '1.2'
            }}>
              {loginConfig.title}
            </h1>
          )}

          {/* Subtitle */}
          {loginConfig.subtitle && (
            <p style={{
              fontSize: '20px',
              marginBottom: '30px',
              textAlign: 'center',
              opacity: 0.9,
              lineHeight: '1.5'
            }}>
              {loginConfig.subtitle}
            </p>
          )}

          {/* Owner Info */}
          {loginConfig.ownerImage && (
            <div style={{ textAlign: 'center', marginTop: '10vh' }}>
              <div style={{
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 16px',
                border: '4px solid white',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
              }}>
                <img 
                  src={loginConfig.ownerImage} 
                  alt={loginConfig.ownerName || 'Owner'} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              {loginConfig.ownerName && (
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '600',
                  marginBottom: '6px'
                }}>
                  {loginConfig.ownerName}
                </h3>
              )}
              {loginConfig.ownerDesignation && (
                <p style={{
                  fontSize: '16px',
                  opacity: 0.9,
                  marginTop: 0
                }}>
                  {loginConfig.ownerDesignation}
                </p>
              )}
            </div>
          )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CustomLogin;
